import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, Mail, Phone, LogOut, MapPin, Calendar } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import PageMeta from "@/components/PageMeta";
import FavoriteButton from "@/components/FavoriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MeinKontoPage() {
  const { t } = useLang();
  const { customer, logout } = useCustomerAuth();
  const [favorites, setFavorites] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/favorites`, { withCredentials: true }),
      axios.get(`${API}/my-inquiries`, { withCredentials: true }),
    ])
      .then(([favRes, inqRes]) => {
        setFavorites(favRes.data);
        setInquiries(inqRes.data);
      })
      .catch((err) => console.warn("Account data load failed:", err?.message))
      .finally(() => setLoading(false));
  }, [customer]);

  if (customer === null) {
    return <div className="pt-40 pb-24 text-center text-white/50">…</div>;
  }

  if (customer === false) {
    return (
      <div data-testid="konto-unauth" className="pt-40 pb-32 max-w-screen-2xl mx-auto px-6 text-center">
        <p className="text-white/60 mb-6">{t("konto.unauth")}</p>
        <Link to="/" className="rh-link text-sm tracking-[0.2em] uppercase" data-testid="konto-back-home">
          ← {t("common.back_to_overview")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="konto-page" className="pt-32 pb-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
      <PageMeta pageKey="konto" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-10 border-b border-white/10">
        <div className="flex items-center gap-4">
          {customer.picture && (
            <img src={customer.picture} alt={customer.name} className="w-14 h-14 rounded-full object-cover border border-white/20" />
          )}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-1" data-testid="konto-welcome-msg">
              {t("konto.welcome")}, {customer.name?.split(" ")[0]}
            </p>
            <p className="font-display text-2xl font-medium tracking-tight">{customer.name}</p>
            <p className="text-sm text-white/50">{customer.email}</p>
          </div>
        </div>
        <button
          onClick={() => { logout(); }}
          data-testid="konto-logout-btn"
          className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 hover:border-white text-xs tracking-[0.18em] uppercase font-medium transition-colors self-start"
        >
          <LogOut size={14} strokeWidth={1.5} /> {t("konto.logout")}
        </button>
      </div>

      {/* Favorites */}
      <section className="mt-14">
        <h2 className="font-display text-2xl md:text-3xl font-light uppercase tracking-tight mb-8 flex items-center gap-3">
          <Heart size={20} strokeWidth={1.5} /> {t("konto.favorites_title")}
        </h2>
        {loading ? (
          <p className="text-white/50">…</p>
        ) : favorites.length === 0 ? (
          <p className="text-white/50" data-testid="konto-favorites-empty">{t("konto.favorites_empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="konto-favorites-list">
            {favorites.map((f) => (
              <Link
                key={f.project.id}
                to={`/projekte/${f.project.slug}`}
                data-testid={`konto-favorite-${f.project.slug}`}
                className="group block border border-white/10 hover:border-white/40 transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={f.project.cover_image} alt={f.project.title} className="w-full h-full object-cover" />
                  <FavoriteButton slug={f.project.slug} className="absolute top-3 right-3" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={11} strokeWidth={1.5} /> {f.project.location.split(",")[0]}</span>
                    <span className="inline-flex items-center gap-1.5"><Calendar size={11} strokeWidth={1.5} /> {f.project.year}</span>
                  </div>
                  <p className="font-display text-lg font-medium tracking-tight">{f.project.title}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* My inquiries */}
      <section className="mt-16">
        <h2 className="font-display text-2xl md:text-3xl font-light uppercase tracking-tight mb-8 flex items-center gap-3">
          <Mail size={20} strokeWidth={1.5} /> {t("konto.inquiries_title")}
        </h2>
        {loading ? (
          <p className="text-white/50">…</p>
        ) : inquiries.length === 0 ? (
          <p className="text-white/50" data-testid="konto-inquiries-empty">{t("konto.inquiries_empty")}</p>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="konto-inquiries-list">
            {inquiries.map((inq) => (
              <div key={inq.id} className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="text-white/85 text-sm leading-relaxed">{inq.message}</p>
                  {inq.phone && (
                    <p className="mt-1.5 text-xs text-white/45 inline-flex items-center gap-1.5">
                      <Phone size={11} strokeWidth={1.5} /> {inq.phone}
                    </p>
                  )}
                </div>
                <span className="text-[11px] tracking-[0.15em] uppercase text-white/40 shrink-0">
                  {new Date(inq.created_at).toLocaleDateString("de-AT")}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
